import React from 'react'
import { FaChartPie, FaChevronDown } from 'react-icons/fa'
import styled from 'styled-components'
import { Collapse } from 'react-bootstrap'
import PillsDataset from '../PillsDataset'
import ClassDistributionCard from '../ClassDistributionCard'

const StepCard = styled.div`
  background: #2a2a2a;
  border: none;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const StepHeader = styled.div<{ $gradient: string }>`
  background: ${(props) => props.$gradient};
  padding: 1rem 1.5rem;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StepContent = styled.div`
  padding: 1.5rem;

  &.distribution-content {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`

const DistributionToggle = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.75rem;
`

const ToggleButton = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  user-select: none;
`

const ToggleOption = styled.div<{ $active: boolean }>`
  padding: 6px 12px;
  color: ${(props) => (props.$active ? '#fff' : 'rgba(255, 255, 255, 0.5)')};
  font-size: 0.85rem;
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
`

const ToggleSlider = styled.div<{ $position: 'left' | 'right' }>`
  position: absolute;
  top: 4px;
  left: 4px;
  bottom: 4px;
  width: calc(50% - 4px);
  background: #2042ba;
  border-radius: 6px;
  transition: transform 0.3s ease;
  transform: translateX(${(props) => (props.$position === 'right' ? '100%' : '0')});
`

const SourceGroup = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .source-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .dataset-container {
    background: rgba(0, 0, 0, 0.2);
    padding: 0.5rem;
    border-radius: 6px;
  }

  &.distribution-group {
    .source-label {
      font-size: 0.9rem;
      color: #fff;
    }

    .distribution-container {
      flex: 1;
      min-height: 0;
    }
  }
`

const DistributionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 0.5rem 0;
  user-select: none;

  .source-label {
    color: #fff;
    font-size: 0.9rem;
    font-weight: 500;
    margin: 0;
  }

  .chevron {
    transition: transform 0.2s ease;
    color: #fff;

    &.open {
      transform: rotate(180deg);
    }
  }
`

interface DataDistributionCardProps {
  gradient: string
  distributionView: 'training' | 'validation'
  onDistributionViewChange: () => void
  isDistributionOpen: boolean
  onDistributionOpenChange: () => void
  datasetNames: string[]
  datasetId?: string
}

const DataDistributionCard: React.FC<DataDistributionCardProps> = ({
  gradient,
  distributionView,
  onDistributionViewChange,
  isDistributionOpen,
  onDistributionOpenChange,
  datasetNames,
  datasetId,
}) => {
  return (
    <StepCard>
      <StepHeader $gradient={gradient}>
        Data Distribution
        <FaChartPie />
      </StepHeader>
      <StepContent className="distribution-content">
        <DistributionToggle>
          <ToggleButton onClick={onDistributionViewChange}>
            <ToggleSlider $position={distributionView === 'validation' ? 'right' : 'left'} />
            <ToggleOption $active={distributionView === 'training'}>Training Data</ToggleOption>
            <ToggleOption $active={distributionView === 'validation'}>Validation Data</ToggleOption>
          </ToggleButton>
        </DistributionToggle>

        <SourceGroup>
          <div className="source-label">Dataset Sources</div>
          <div className="dataset-container">
            <PillsDataset datasetNames={datasetNames} tiny={true} />
          </div>
        </SourceGroup>

        <SourceGroup className="distribution-group">
          <DistributionHeader onClick={onDistributionOpenChange}>
            <div className="source-label">Class Distribution</div>
            <FaChevronDown className={`chevron ${isDistributionOpen ? 'open' : ''}`} />
          </DistributionHeader>
          <Collapse in={isDistributionOpen}>
            <div>
              <div className="distribution-container">
                <ClassDistributionCard
                  useSources={datasetNames}
                  title=""
                  height="350px"
                  datasetId={datasetId}
                />
              </div>
            </div>
          </Collapse>
        </SourceGroup>
      </StepContent>
    </StepCard>
  )
}

export default DataDistributionCard
